import {store} from '../store';
import type {AuthResponse} from '../types/api';
import type {VwpAuthUser} from '../store/generated/api';
import {emptyApi as injectedRtkApi,} from '../store/generated/api';
import {TokenUtils} from '../utils/authUtils';
import {tokenProvider} from './tokenProvider';
import {config} from '../config';
import {toUrlSafeBase64} from '../utils/base64Utils';

interface LoginStateData {
    redirectUrl: string;
}

export type ScreenHint = 'sign-up' | 'sign-in';

export class AuthService {
    async initiateLogin(redirectUrl?: string, screenHint?: ScreenHint): Promise<void> {
        const frontendUrl = config.frontendUrl || window.location.origin;
        const finalRedirectUrl = redirectUrl || `${frontendUrl}${window.location.pathname}${window.location.search}`;
        const fullRedirectUrl = finalRedirectUrl.startsWith('http') ? finalRedirectUrl : `${frontendUrl}${finalRedirectUrl}`;

        const stateData: LoginStateData = {redirectUrl: fullRedirectUrl};
        const encodedState = toUrlSafeBase64(JSON.stringify(stateData));

        const screenHintParam = screenHint ? `&screenHint=${screenHint}` : '';

        window.location.href = `${config.backendUrl}/auth/login?state=${encodedState}${screenHintParam}`;
    }

    async handleTokensFromUrl(): Promise<AuthResponse | null> {
        const urlParams = new URLSearchParams(window.location.search);
        const token = urlParams.get('token');
        const refreshToken = urlParams.get('refresh_token');
        const error = urlParams.get('error');

        if (error) {
            throw new Error(`Authentication error: ${error}`);
        }

        if (!token) return null;

        tokenProvider.storeTokensFromCallback(token, refreshToken);
        try {
            const user = await this.getCurrentUser();
            return {
                authenticated: true,
                user: user || undefined,
                token
            };
        } catch (error) {
            const userFromToken = TokenUtils.createUserFromToken(token);
            return {
                authenticated: true,
                user: userFromToken || undefined,
                token
            };
        }
    }

    async getCurrentUser(): Promise<VwpAuthUser | null> {
        try {
            const result = await store.dispatch(injectedRtkApi.endpoints.getCurrentUser.initiate());

            if (result.error) {
                throw new Error(result.error as string);
            }

            return result.data?.user || null;
        } catch (error) {
            return null;
        }
    }

    async refreshToken(): Promise<boolean> {
        try {
            const storedRefreshToken = this.getRefreshToken();
            const result = await store.dispatch(injectedRtkApi.endpoints.refreshToken.initiate({body: {refreshToken: storedRefreshToken || ''}}));

            if (result.error) {
                throw new Error(result.error as string);
            }

            const token = result.data?.token;
            const expires_in = result.data?.expires_in;
            const refresh_token = result.data?.refresh_token;

            if (token) {
                tokenProvider.updateTokensFromRefresh(token, expires_in, refresh_token);
                return true;
            }

            return false;
        } catch (error) {
            return false;
        }
    }

    async logout(): Promise<void> {
        try {
            const result = await store.dispatch(injectedRtkApi.endpoints.logout.initiate());
            const redirectUrl = result.data?.redirectUrl;

            if (redirectUrl) {
                window.location.href = redirectUrl;
            }
        } finally {
            tokenProvider.clearTokens();
        }
    }

    isAuthenticated(): boolean {
        return tokenProvider.isAuthenticated();
    }

    getAuthToken(): string | null {
        return tokenProvider.getAccessToken();
    }

    getRefreshToken(): string | null {
        return tokenProvider.getRefreshToken();
    }

    getTokenExpiry(): number | null {
        return tokenProvider.getTokenExpiry();
    }

    async initializeAuth(): Promise<AuthResponse | null> {
        const token = this.getAuthToken();
        const tokenExpiry = this.getTokenExpiry();

        if (!token) return null;

        if (tokenExpiry && Date.now() > tokenExpiry) {
            if (await this.refreshToken()) {
                const newToken = this.getAuthToken();
                if (newToken) {
                    try {
                        const user = await this.getCurrentUser();
                        if (user) {
                            return {authenticated: true, user, token: newToken};
                        }
                    } catch (refreshError) {
                        const userFromToken = TokenUtils.createUserFromToken(newToken);
                        if (userFromToken) {
                            return {authenticated: true, user: userFromToken, token: newToken};
                        }
                    }
                }
            }

            const userFromToken = TokenUtils.createUserFromToken(token);
            if (userFromToken) {
                return {authenticated: true, user: userFromToken, token};
            }

            tokenProvider.clearTokens();
            return null;
        }

        try {
            const user = await this.getCurrentUser();
            if (user) {
                return {authenticated: true, user, token};
            }
        } catch (error) {
            const userFromToken = TokenUtils.createUserFromToken(token);
            if (userFromToken) {
                return {authenticated: true, user: userFromToken, token};
            }

            tokenProvider.clearTokens();
        }

        return null;
    }
}

export const authServiceRtk = new AuthService();

export const createAuthServiceRtk = (): AuthService => new AuthService();